import type { InstrumentName } from '@tunasong/audio-ui'
import { midiToNoteLabel } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { useEffect, useRef } from 'react'
import { usePlayNotes } from '../player/piano-player.hook.js'
import type { Stab } from '../player/stab-scheduler.js'

/**
 * Pitch Trainer audition: the count-in that says "here it comes", and the reference the singer
 * reproduces.
 *
 * The shape is a teacher's cue — *"here it comes — [plays] — sing"* — and the ordering is the
 * whole of it. An earlier build put four pitched piano clicks *after* the reference, so the
 * singer waited 2.9 s with four more piano notes near soprano range poured over the pitch they
 * were trying to hold. Interpolated pitched tones erode pitch memory, and clicks sharing the
 * reference's timbre read as more notes of the phrase. Now: two woodblock knocks, then the
 * reference, then one beat of silence, then sing.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §6
 */

/** The count-in tempo. A moderate walking pulse — fast enough not to drag, slow enough to sing to. */
export const countInBpm = 100

const beatMs = 60000 / countInBpm

/**
 * The count-in is unpitched, and deliberately not the instrument the reference uses.
 *
 * A woodblock is a knock: it carries the beat without putting a pitch anywhere near the one the
 * singer is holding, and its timbre cannot be mistaken for another note of the phrase.
 */
export const countInInstrument: InstrumentName = 'woodblock'

/**
 * The count-in's pitch. On a woodblock this is a knock rather than a note — the sample is
 * unpitched percussion — but the engine still wants a note name to trigger it, and a single one
 * for both clicks means no accent pattern and therefore no metric instruction.
 */
const COUNT_IN_NOTE = 'C5' as NoteName

/** Short enough to be a knock and not a tone. */
const COUNT_IN_DURATION = '32n' as const

/** How long the count-in sounds, and so how long the capture window waits for it. */
export const countInMs = 2 * beatMs

/**
 * One beat of silence between the reference and the singer's entry — the breath.
 *
 * It does double duty: musically it is the gap a teacher leaves before "sing", and technically it
 * is the room tone §5.1 measures the noise floor from (which needs at least 500 ms). Nothing
 * pitched sounds inside it, which is the point — it is the only thing between what the singer
 * heard and what they are about to sing.
 */
export const leadInMs = beatMs

/**
 * Two clicks, one pitch, equal weight.
 *
 * Two, because two clicks say "listen now" while a full bar is an ensemble convention for
 * starting together — and rhythm is not scored here (§2), so the other three beats bought
 * nothing and cost the singer the pitch. Deliberately NOT `buildClickStabs`: that exists for the
 * Rhythm Trainer's pitch-coded C6 / A5 / G5 accent tiers, which are precisely what must not sound
 * next to a pitch held in memory.
 */
export const buildCountInStabs = (): Stab[] =>
  [0, beatMs].map(delayMs => ({
    events: [{ type: 'note' as const, note: COUNT_IN_NOTE, duration: COUNT_IN_DURATION }],
    delayMs,
  }))

/**
 * The reference, one note at a time. Melodic, never harmonic: the singer reproduces a line, and
 * a block sonority would be a different exercise.
 */
export const buildReferenceStabs = (midis: number[], noteMs: number): Stab[] =>
  midis.map((midi, index) => ({
    events: [{ type: 'note' as const, note: midiToNoteLabel(midi) as NoteName, duration: '4n' }],
    delayMs: index * noteMs,
  }))

/** How long the reference sounds — the last note included, which is what sizes the capture. */
export const referenceDurationMs = (midis: number[], noteMs: number): number => midis.length * noteMs

export interface PitchAudition {
  /** Sound the reference. Resolves when the last note has had its full length. */
  playReference: (midis: number[], noteMs: number) => Promise<void>
  /** Sound the count-in. Resolves after the second knock. */
  playCountIn: () => Promise<void>
}

/**
 * Awaitable audition, on two instruments.
 *
 * `useStabScheduler` is fire-and-forget, and the capture window has to know when playback has
 * *finished* — it opens the microphone immediately afterwards. Two `usePlayNotes` instances,
 * because the count-in and the reference must not share a timbre; each routes to its own
 * registered instrument (@see useInstrument — a bare `engine.play(notes)` would send both to
 * whichever mounted first, and the failure is silent).
 */
export const usePitchAudition = (): PitchAudition => {
  const { playNotes: playPiano } = usePlayNotes()
  const { playNotes: playWoodblock } = usePlayNotes(countInInstrument)
  const timeouts = useRef<number[]>([])

  useEffect(
    () => () => {
      for (const handle of timeouts.current) {
        window.clearTimeout(handle)
      }
      timeouts.current = []
    },
    []
  )

  const run = (stabs: Stab[], totalMs: number, play: (events: Stab['events']) => void) =>
    new Promise<void>(resolve => {
      for (const stab of stabs) {
        timeouts.current.push(window.setTimeout(() => play(stab.events), stab.delayMs))
      }
      timeouts.current.push(window.setTimeout(resolve, totalMs))
    })

  return {
    playReference: (midis, noteMs) =>
      run(buildReferenceStabs(midis, noteMs), referenceDurationMs(midis, noteMs), playPiano),
    playCountIn: () => run(buildCountInStabs(), countInMs, playWoodblock),
  }
}
